/**
 * Camera Projection Utilities
 * 
 * Handles 3D point projection to camera image planes using actual reconstruction data.
 * Works with COLMAP camera model and quaternion rotations.
 */

/**
 * Convert quaternion [w, x, y, z] to 3x3 rotation matrix
 */
/**
 * Convert quaternion to 3x3 rotation matrix
 * COLMAP uses Hamilton convention: [w, x, y, z] where w is scalar part
 * But the stored rotation is camera-to-world, so when creating matrix, we interpret it that way
 */
export function quaternionToMatrix(q: [number, number, number, number]): number[][] {
  // Store rotation is [QW, QX, QY, QZ] (Hamilton order from COLMAP cameras.txt)
  // This represents camera-to-world rotation
  const [w, x, y, z] = q
  
  const matrix = [
    [1 - 2*(y*y + z*z), 2*(x*y - z*w), 2*(x*z + y*w)],
    [2*(x*y + z*w), 1 - 2*(x*x + z*z), 2*(y*z - x*w)],
    [2*(x*z - y*w), 2*(y*z + x*w), 1 - 2*(x*x + y*y)]
  ]
  
  return matrix
}

/**
 * Multiply 3x3 matrix by 3D vector
 */
function matrixVecMultiply(matrix: number[][], vec: [number, number, number]): [number, number, number] {
  return [
    matrix[0][0] * vec[0] + matrix[0][1] * vec[1] + matrix[0][2] * vec[2],
    matrix[1][0] * vec[0] + matrix[1][1] * vec[1] + matrix[1][2] * vec[2],
    matrix[2][0] * vec[0] + matrix[2][1] * vec[1] + matrix[2][2] * vec[2]
  ]
}

/**
 * Whether to apply Brown-Conrady lens distortion correction.
 * Set to false to revert to simple pinhole model.
 */
const ENABLE_DISTORTION_CORRECTION = true

/**
 * Project a 3D point to a camera's image plane
 * 
 * Uses COLMAP camera model: P_cam = R @ P + T
 * where R is the rotation matrix from quaternion, T is translation, and P is world point
 * 
 * When ENABLE_DISTORTION_CORRECTION is true, applies the Brown-Conrady (OPENCV) distortion
 * model using k1, k2 (radial) and p1, p2 (tangential) coefficients from camera intrinsics.
 * 
 * @param point3D - 3D point in world coordinates [x, y, z]
 * @param camera - Camera with position, rotation (quaternion [w,x,y,z]), and intrinsics
 * @returns [x, y] image coordinates, or null if point is behind camera
 */
export function project3DPointToCamera(
  point3D: [number, number, number],
  camera: any
): [number, number] | null {
  const P = point3D as [number, number, number]
  const R_stored = camera.rotation as [number, number, number, number]  // [qw, qx, qy, qz]
  const T = camera.position as [number, number, number]
  const intrinsics = camera.intrinsics
  
  // Get rotation matrix from quaternion (does NOT need transpose)
  const R = quaternionToMatrix(R_stored)
  
  // Transform to camera frame: P_cam = R @ P + T
  const P_cam = matrixVecMultiply(R, P)
  P_cam[0] += T[0]
  P_cam[1] += T[1]
  P_cam[2] += T[2]
  
  // Check if point is in front of camera (Z > 0 in camera frame)
  if (P_cam[2] <= 0) {
    return null  // Point behind camera
  }
  
  // Project to image plane using camera intrinsics
  const fx = intrinsics.fx
  const fy = intrinsics.fy
  const cx = intrinsics.cx
  const cy = intrinsics.cy
  
  // Normalized camera coordinates
  const x = P_cam[0] / P_cam[2]
  const y = P_cam[1] / P_cam[2]
  
  let xd = x
  let yd = y
  
  // Apply Brown-Conrady (OPENCV) distortion model if enabled and coefficients available
  if (ENABLE_DISTORTION_CORRECTION && intrinsics.k1 !== undefined) {
    const k1 = intrinsics.k1 || 0
    const k2 = intrinsics.k2 || 0
    const p1 = intrinsics.p1 || 0
    const p2 = intrinsics.p2 || 0
    
    const r2 = x * x + y * y
    const r4 = r2 * r2
    const radial = 1 + k1 * r2 + k2 * r4
    
    // Radial + tangential distortion
    xd = x * radial + 2 * p1 * x * y + p2 * (r2 + 2 * x * x)
    yd = y * radial + p1 * (r2 + 2 * y * y) + 2 * p2 * x * y
  }
  
  // Project distorted normalized coordinates to pixel
  const px = xd * fx + cx
  const py = yd * fy + cy
  
  return [px, py]
}

/**
 * Check if a projected point is within image bounds
 */
export function isInBounds(
  point2D: [number, number],
  intrinsics: any,
  margin: number = 0
): boolean {
  const [x, y] = point2D
  const { width, height } = intrinsics
  
  return x >= -margin && x < width + margin && 
         y >= -margin && y < height + margin
}

/**
 * Find nearest observed point in a camera frame to a double-clicked pixel
 * 
 * @param clickPixel - [x, y] pixel coordinates where user double-clicked
 * @param camera - Camera frame with observed_points
 * @returns The matched observation (with point_id and pixel), or null if no match
 */
export function findNearestObservedPoint(
  clickPixel: [number, number],
  camera: any,
  maxDistance: number = 50  // pixels - stricter to avoid finding wrong points
): any | null {
  if (!camera.observed_points || camera.observed_points.length === 0) {
    console.log(`      ℹ️ No observed_points in camera data (array ${camera.observed_points ? 'exists but empty' : 'missing'})`)
    return null
  }
  
  console.log(`      🔍 Searching ${camera.observed_points.length} observed points within ${maxDistance}px of double-click`)
  
  // Find all nearby points sorted by distance
  const nearby = camera.observed_points.map((obs: any) => {
    const [px, py] = obs.pixel
    const dx = px - clickPixel[0]
    const dy = py - clickPixel[1]
    const dist = Math.sqrt(dx * dx + dy * dy)
    return { obs, dist, px, py }
  }).sort((a: any, b: any) => a.dist - b.dist)
  
  // Show top 3 nearest points
  for (let i = 0; i < Math.min(3, nearby.length); i++) {
    const { obs, dist, px, py } = nearby[i]
    const marker = dist <= maxDistance ? '✓' : 'ℹ️'
    console.log(`         ${marker} #${i+1}: [${px.toFixed(1)}, ${py.toFixed(1)}] dist=${dist.toFixed(1)}px id=${obs.point_id}`)
  }
  
  // Return first one within threshold
  if (nearby.length > 0 && nearby[0].dist <= maxDistance) {
    return nearby[0].obs
  }
  
  return null
}

/**
 * Find the 3D point with given ID in the points3D array
 */
export function find3DPoint(pointId: number, points3D: any[]): any | null {
  return points3D.find((p: any) => p.id === pointId) || null
}

/**
 * Main workflow: Find which connected cameras can see the clicked 3D point
 * 
 * Steps:
 * 1. Find nearest observed point in selected camera to clicked pixel
 * 2. Get point_id from that observation
 * 3. Find the 3D point in points3D array
 * 4. Project that 3D point to all connected cameras
 * 5. Return frame indices where projection is in bounds
 * 
 * @param clickPixel - Where user clicked [x, y]
 * @param selectedFrameIndex - Which frame the user is looking at
 * @param cameras - All cameras with observed_points
 * @param points3D - All 3D points with IDs
 * @param offsetMap - Connectivity information
 * @returns Set of frame indices that can see the clicked point
 */
export function findConnectedCamerasThatSee3DPoint(
  clickPixel: [number, number],
  selectedFrameIndex: number,
  cameras: any[],
  points3D: any[],
  offsetMap: any
): Set<number> {
  const result = new Set<number>()
  
  // Step 1: Get selected camera
  const selectedCamera = cameras[selectedFrameIndex]
  if (!selectedCamera) {
    console.warn(`Camera ${selectedFrameIndex} not found`)
    return result
  }
  
  // Step 2: Find nearest observed point
  const observation = findNearestObservedPoint(clickPixel, selectedCamera)
  if (!observation) {
    console.warn(`No observed point near clicked pixel [${clickPixel[0]}, ${clickPixel[1]}]`)
    return result
  }
  
  const pointId = observation.point_id
  console.log(`✓ Found observed point: ID ${pointId} at pixel [${observation.pixel[0]}, ${observation.pixel[1]}]`)
  
  // Step 3: Find 3D point
  const point3D = find3DPoint(pointId, points3D)
  if (!point3D) {
    console.warn(`3D point ${pointId} not found`)
    return result
  }
  
  const point3DCoords = point3D.xyz as [number, number, number]
  console.log(`✓ Found 3D point: [${point3DCoords[0].toFixed(2)}, ${point3DCoords[1].toFixed(2)}, ${point3DCoords[2].toFixed(2)}]`)
  
  // Step 4: Get connected cameras
  const connectedIndices = offsetMap.connectivity?.[selectedFrameIndex] || []
  if (connectedIndices.length === 0) {
    console.warn(`No connected cameras for frame ${selectedFrameIndex}`)
    return result
  }
  
  console.log(`Checking ${connectedIndices.length} connected cameras: ${connectedIndices.join(', ')}`)
  
  // Step 5: Project to each connected camera
  for (const targetFrameIndex of connectedIndices) {
    const targetCamera = cameras[targetFrameIndex]
    if (!targetCamera) continue
    
    const projected = project3DPointToCamera(point3DCoords, targetCamera)
    if (!projected) {
      // Point is behind this camera
      console.log(`  Frame ${targetFrameIndex}: point behind camera`)
      continue
    }
    
    const inBounds = isInBounds(projected, targetCamera.intrinsics)
    console.log(`  Frame ${targetFrameIndex}: projected to [${projected[0].toFixed(1)}, ${projected[1].toFixed(1)}] → ${inBounds ? '✓ IN BOUNDS' : '✗ out of bounds'}`)
    
    if (inBounds) {
      result.add(targetFrameIndex)
    }
  }
  
  console.log(`Result: ${result.size} cameras can see this point: [${Array.from(result).join(', ')}]`)
  return result
}

/**
 * Check if a projected point is within the center ±20% bounds of the target camera
 * 
 * @param projectedX - X coordinate of projected point
 * @param projectedY - Y coordinate of projected point
 * @param intrinsics - Target camera intrinsics
 * @param tolerance - Tolerance as fraction of image size (default 0.2 = ±20%)
 * @returns true if within center bounds
 */
export function isProjectedPointInCenterBounds(
  projectedX: number,
  projectedY: number,
  intrinsics: any,
  tolerance: number = 0.2
): boolean {
  const cx = intrinsics.cx
  const cy = intrinsics.cy
  const width = intrinsics.width
  const height = intrinsics.height
  
  // Center ±tolerance% bounds
  const xMin = cx - (width / 2) * tolerance
  const xMax = cx + (width / 2) * tolerance
  const yMin = cy - (height / 2) * tolerance
  const yMax = cy + (height / 2) * tolerance
  
  return projectedX >= xMin && projectedX <= xMax && projectedY >= yMin && projectedY <= yMax
}

/**
 * Find all cameras that can see a double-clicked point on the selected frame
 * 
 * Workflow:
 * 1. Find nearest observed point in selected camera to double-click pixel
 * 2. Extract point_id from that observation
 * 3. Look up 3D coordinates in points3D array
 * 4. Project that 3D point to each connected camera
 * 5. Return cameras where projection is in-bounds
 * 
 * @param pixelX - Double-click X coordinate on selected frame image
 * @param pixelY - Double-click Y coordinate on selected frame image
 * @param selectedFrameIndex - Index of the selected/double-clicked frame
 * @param cameras - Array of all camera data
 * @param points3D - Array of 3D points with IDs
 * @param offsetMap - Offset map containing connectivity info
 * @returns Set of frame indices whose cameras can see the double-clicked point
 */
export function findHighlightedCameras(
  pixelX: number,
  pixelY: number,
  selectedFrameIndex: number,
  cameras: any[],
  points3D: any[],
  offsetMap: any
): {
  frameIndices: Set<number>;
  projections: Map<number, [number, number]>;
  point3D: [number, number, number] | null;
} {
  const highlighted = new Set<number>()
  const projections = new Map<number, [number, number]>()
  
  if (!offsetMap?.connectivity || !cameras?.[selectedFrameIndex]) {
    return { frameIndices: highlighted, projections, point3D: null }
  }
  
  const selectedCamera = cameras[selectedFrameIndex]
  const clickPixel: [number, number] = [pixelX, pixelY]
  
  console.log(`\n🖱️ [Frame tap point] Frame ${selectedFrameIndex} at [${pixelX.toFixed(1)}, ${pixelY.toFixed(1)}]`)
  
  // Step 1: Find nearest observed point
  const observation = findNearestObservedPoint(clickPixel, selectedCamera)  // Uses default 100px radius
  if (!observation) {
    console.log(`   ❌ No observed point near click`)
    return { frameIndices: highlighted, projections, point3D: null }
  }
  
  const pointId = observation.point_id
  const deltaX = observation.pixel[0] - pixelX
  const deltaY = observation.pixel[1] - pixelY
  const clickOffset = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
  
  console.log(`   ✓ Found observed point ID: ${pointId}`)
  if (clickOffset > 0.5) {
    console.log(`   ⚠️  OFFSET: You clicked [${pixelX.toFixed(1)}, ${pixelY.toFixed(1)}] but nearest observed point is at [${observation.pixel[0].toFixed(1)}, ${observation.pixel[1].toFixed(1)}] (${clickOffset.toFixed(1)}px away)`)
  }
  
  // Check which frames actually have this point in their observed_points
  // console.log(`   🔍 Checking which frames have point ${pointId} in their observed_points:`)
  for (let i = 0; i < Math.min(15, cameras.length); i++) {
    const cam = cameras[i]
    if (!cam || !cam.observed_points) continue
    const hasPoint = cam.observed_points.some((obs: any) => obs.point_id === pointId)
    const marker = hasPoint ? '✓' : '✗'
    const pixelInfo = cam.observed_points.find((obs: any) => obs.point_id === pointId)
    const pixelStr = pixelInfo ? ` at [${pixelInfo.pixel[0].toFixed(0)}, ${pixelInfo.pixel[1].toFixed(0)}]` : ''
    // console.log(`      Frame ${i}: ${marker}${pixelStr}`)
  }
  
  // Step 2: Find 3D point
  const point3D = find3DPoint(pointId, points3D)
  if (!point3D) {
    console.log(`   ❌ 3D point ${pointId} not found in points3D`)
    return { frameIndices: highlighted, projections, point3D: null }
  }
  
  // Extract 3D coordinates - the structure has 'position' property
  const coords = point3D.position as [number, number, number]
  if (!coords || coords.length !== 3) {
    console.log(`   ❌ Invalid position in 3D point:`, point3D.position)
    return { frameIndices: highlighted, projections, point3D: null }
  }
  
  console.log(`   ✓ 3D coords: [${coords[0].toFixed(2)}, ${coords[1].toFixed(2)}, ${coords[2].toFixed(2)}]`)
  
  // VALIDATION: Test if projection formula works on Frame 0 (known good observation)
  console.log(`\n   🔍 [VALIDATION - Camera Model Check]`)
  const validationCamera = cameras[selectedFrameIndex]
  if (validationCamera) {
    const projectedOnFrame0 = project3DPointToCamera(coords, validationCamera)
    if (projectedOnFrame0) {
      const error = Math.sqrt(
        Math.pow(projectedOnFrame0[0] - clickPixel[0], 2) +
        Math.pow(projectedOnFrame0[1] - clickPixel[1], 2)
      )
      console.log(`      Observed pixel (clicked point): [${clickPixel[0].toFixed(1)}, ${clickPixel[1].toFixed(1)}]`)
      console.log(`      Projected on Frame 0: [${projectedOnFrame0[0].toFixed(1)}, ${projectedOnFrame0[1].toFixed(1)}]`)
      console.log(`      Reprojection error: ${error.toFixed(1)}px`)
      
      if (error > 30) {
        console.log(`      ⚠️  CAMERA MODEL FORMULA IS WRONG (error > 30px)`)
      } else {
        console.log(`      ✅ CAMERA MODEL FORMULA IS CORRECT`)
      }
    } else {
      console.log(`      ❌ Point projects behind camera on Frame 0 - formula is inverted or has sign error`)
    }
  }
  
  
  // Step 3: Get connected cameras
  const connectedFrameIndices = offsetMap.connectivity[selectedFrameIndex] || []
  console.log(`   📡 Checking ${connectedFrameIndices.length} connected cameras`)
  
  // Step 4: Project to each connected camera
  let inBoundsCount = 0
  let outOfBoundsCount = 0
  let behindCameraCount = 0
  
  // Always include the selected frame itself
  const framesToCheck = [selectedFrameIndex, ...connectedFrameIndices]
  console.log(`   📡 Total frames to check (including self): ${framesToCheck.length}`)
  console.log(`   🔍 [PROJECTION DEBUG] 3D point: [${coords[0].toFixed(3)}, ${coords[1].toFixed(3)}, ${coords[2].toFixed(3)}]`)
  
  for (const targetFrameIndex of framesToCheck) {
    const targetCamera = cameras[targetFrameIndex]
    if (!targetCamera) {
      // console.log(`      ❌ Frame ${targetFrameIndex}: Camera object not found`)
      continue
    }
    
    // Calculate distance for debugging
    const camPos = targetCamera.position
    const dist = Math.sqrt(
      Math.pow(coords[0] - camPos[0], 2) +
      Math.pow(coords[1] - camPos[1], 2) +
      Math.pow(coords[2] - camPos[2], 2)
    )
    
    // Special case: if this is the selected frame, use the observation point directly
    if (targetFrameIndex === selectedFrameIndex) {
      highlighted.add(targetFrameIndex)
      projections.set(targetFrameIndex, observation.pixel as [number, number])
      inBoundsCount++
      // console.log(`      ✅ Frame ${targetFrameIndex}: [${observation.pixel[0].toFixed(1)}, ${observation.pixel[1].toFixed(1)}] (selected frame, dist=${dist.toFixed(2)})`)
      continue
    }
    
    // Project 3D point to target camera (ray cast through camera)
    const projected = project3DPointToCamera(coords, targetCamera)
    
    if (!projected) {
      behindCameraCount++
      console.log(`      ⬜ Frame ${targetFrameIndex}: point behind camera (dist=${dist.toFixed(2)})`)
      continue
    }
    
    // Check if in image bounds
    const inBounds = isInBounds(projected, targetCamera.intrinsics)
    const [projX, projY] = projected
    const { width, height } = targetCamera.intrinsics
    
    
    
    if (inBounds) {
      highlighted.add(targetFrameIndex)
      projections.set(targetFrameIndex, projected)
      inBoundsCount++
      // console.log(`      ✅ Frame ${targetFrameIndex}: [${projX.toFixed(1)}, ${projY.toFixed(1)}] in ${width}×${height} (dist=${dist.toFixed(2)})`)
    } else {
      outOfBoundsCount++
      // console.log(`      ❌ Frame ${targetFrameIndex}: [${projX.toFixed(1)}, ${projY.toFixed(1)}] OUT of ${width}×${height} (dist=${dist.toFixed(2)})`)
    }
  }
  
  console.log(`\n   📊 [PROJECTION SUMMARY]`)
  console.log(`      ✅ In-bounds: ${inBoundsCount}`)
  console.log(`      ❌ Out-of-bounds: ${outOfBoundsCount}`)
  console.log(`      ⬜ Behind-camera: ${behindCameraCount}`)
  console.log(`      📂  Projections map keys: [${Array.from(projections.keys()).join(', ')}]`)
  console.log(`      📂  Highlighted cameras: [${Array.from(highlighted).join(', ')}]`)
  
  return { frameIndices: highlighted, projections, point3D: coords }
}

/**
 * Check if a projected point is within the central 30% area of the image (±30% from center)
 * 
 * @param projected - [x, y] projected point in image coordinates
 * @param intrinsics - Camera intrinsics with width and height
 * @param threshold - Percentage threshold (0.3 = ±30% from center)
 * @returns true if point is in central region
 */
export function isProjectionCentral(
  projected: [number, number],
  intrinsics: any,
  threshold: number = 0.3
): boolean {
  const [x, y] = projected
  const centerX = intrinsics.width / 2
  const centerY = intrinsics.height / 2
  
  // Calculate allowed deviation from center
  const allowedX = (intrinsics.width / 2) * threshold
  const allowedY = (intrinsics.height / 2) * threshold
  
  // Check if within central region (±threshold% from center)
  return Math.abs(x - centerX) <= allowedX && Math.abs(y - centerY) <= allowedY
}

/**
 * Calculate camera visibility for a tap/click location
 * 
 * Extracts the camera calculation logic from the double-click handler for reusability.
 * Converts display coordinates to image coordinates and finds highlighted cameras.
 * 
 * @param clickX - X coordinate of click in display pixels (relative to container)
 * @param clickY - Y coordinate of click in display pixels (relative to container)
 * @param containerRect - Bounding rectangle of the clicked container
 * @param frameCamera - Camera data for the frame being clicked
 * @param cameras - Array of all camera data
 * @param points3D - Array of 3D points
 * @param offsetMap - Connectivity information
 * @param frameIndex - Index of the clicked frame
 * @returns Object with highlightedCameras Set and cameraCount
 */
export function calculateCameraVisibility(
  clickX: number,
  clickY: number,
  containerRect: DOMRect,
  frameCamera: any,
  cameras: any[],
  points3D: any[],
  offsetMap: any,
  frameIndex: number
): { 
  highlightedCameras: Set<number>; 
  cameraCount: number;
  projections: Map<number, [number, number]>;
  point3D: [number, number, number] | null;
} {
  console.log(`\n[NORMALIZATION] Converting display coordinates to image pixels`)
  console.log(`  Display input: clickX=${clickX.toFixed(1)}, clickY=${clickY.toFixed(1)}`)
  console.log(`  Container rect: ${containerRect.width.toFixed(0)}×${containerRect.height.toFixed(0)}px`)
  console.log(`  Frame intrinsics: ${frameCamera.intrinsics.width}×${frameCamera.intrinsics.height}px`)
  
  // Convert display pixels to actual image pixels
  const pixelX = (clickX / containerRect.width) * frameCamera.intrinsics.width
  const pixelY = (clickY / containerRect.height) * frameCamera.intrinsics.height
  
  console.log(`  ✓ Ratio X: (${clickX.toFixed(1)} / ${containerRect.width.toFixed(0)}) × ${frameCamera.intrinsics.width} = ${pixelX.toFixed(1)}`)
  console.log(`  ✓ Ratio Y: (${clickY.toFixed(1)} / ${containerRect.height.toFixed(0)}) × ${frameCamera.intrinsics.height} = ${pixelY.toFixed(1)}`)
  console.log(`  Image pixel OUTPUT: (${pixelX.toFixed(1)}, ${pixelY.toFixed(1)})`)

  // Find cameras that can see the clicked 3D point
  const result = findHighlightedCameras(
    pixelX,
    pixelY,
    frameIndex,
    cameras,
    points3D,
    offsetMap
  )

  // CRITICAL: Only return cameras that have valid projections in the map
  // Filter out any cameras that don't have a projection entry
  const validHighlightedCameras = new Set(
    Array.from(result.frameIndices).filter(frameIdx => result.projections.has(frameIdx))
  )

  console.log(`\n📊 [3D LOOKUP RESULT]`)
  console.log(`  Found 3D point: ${result.point3D ? `(${result.point3D[0].toFixed(1)}, ${result.point3D[1].toFixed(1)}, ${result.point3D[2].toFixed(1)})` : 'null'}`)
  console.log(`  Cameras with projections: ${result.frameIndices.size}`)
  console.log(`  Cameras with VALID projections: ${validHighlightedCameras.size}`)
  console.log(`${'═'.repeat(80)}\n`)

  return {
    highlightedCameras: validHighlightedCameras,
    cameraCount: validHighlightedCameras.size,
    projections: result.projections,
    point3D: result.point3D
  }
}

/**
 * Find the best camera from a set of highlighted cameras based on:
 * 1. Central projection (±30% from image center) - prioritized
 * 2. Nearest 3D distance to the point
 * 
 * @param point3D - The 3D point coordinates (from calculateCameraVisibility result)
 * @param highlightedCameras - Set of camera indices that can see the point
 * @param cameras - All cameras array
 * @returns The best camera index, or null if none found
 */
export function findBestHighlightedCamera(
  point3D: [number, number, number],
  highlightedCameras: Set<number>,
  cameras: any[]
): number | null {
  if (highlightedCameras.size === 0 || !point3D) {
    return null
  }

  const clickedPoint3D = point3D

  // Phase 1: Filter to cameras with central projection (±30% from center)
  const centralCameras: Array<{ cameraIndex: number; distance3d: number; projected: [number, number] }> = []

  for (const cameraIndex of highlightedCameras) {
    const camera = cameras[cameraIndex]
    if (!camera) continue

    // Project 3D point to this camera
    const projected = project3DPointToCamera(clickedPoint3D, camera)
    if (!projected) continue

    // Check if projection is in central 30% area
    if (isProjectionCentral(projected, camera.intrinsics, 0.3)) {
      // Calculate 3D distance from clicked point to camera position
      const [px, py, pz] = clickedPoint3D
      const [cx, cy, cz] = camera.position as [number, number, number]
      const distance3d = Math.sqrt((px - cx) ** 2 + (py - cy) ** 2 + (pz - cz) ** 2)

      centralCameras.push({
        cameraIndex,
        distance3d,
        projected
      })

      console.log(`      Camera ${cameraIndex}: projected [${projected[0].toFixed(1)}, ${projected[1].toFixed(1)}] → 3D distance: ${distance3d.toFixed(2)}m (CENTRAL ✓)`)
    } else {
      const [px, py, pz] = clickedPoint3D
      const [cx, cy, cz] = camera.position as [number, number, number]
      const distance3d = Math.sqrt((px - cx) ** 2 + (py - cy) ** 2 + (pz - cz) ** 2)

      console.log(`      Camera ${cameraIndex}: projected [${projected[0].toFixed(1)}, ${projected[1].toFixed(1)}] → 3D distance: ${distance3d.toFixed(2)}m (out of central)`)
    }
  }

  // Phase 2: Select best camera
  let bestCamera: number | null = null
  let bestDistance: number = Infinity

  if (centralCameras.length > 0) {
    // Use central cameras: pick the nearest in 3D space
    for (const cam of centralCameras) {
      if (cam.distance3d < bestDistance) {
        bestDistance = cam.distance3d
        bestCamera = cam.cameraIndex
      }
    }
    console.log(`   🎯 [CENTRAL SELECTION] Best camera: ${bestCamera} (3D distance: ${bestDistance.toFixed(2)}m, ${centralCameras.length} central cameras)`)
  } else {
    // Fallback: Use all highlighted cameras, pick nearest in 3D space
    console.log(`   ⚠️ [NO CENTRAL CAMERAS] Falling back to all highlighted cameras`)

    for (const cameraIndex of highlightedCameras) {
      const camera = cameras[cameraIndex]
      if (!camera) continue

      const projected = project3DPointToCamera(clickedPoint3D, camera)
      if (!projected) continue

      // Calculate 3D distance only (no centrality factor)
      const [px, py, pz] = clickedPoint3D
      const [cx, cy, cz] = camera.position as [number, number, number]
      const distance3d = Math.sqrt((px - cx) ** 2 + (py - cy) ** 2 + (pz - cz) ** 2)

      console.log(`      Camera ${cameraIndex}: 3D distance: ${distance3d.toFixed(2)}m`)

      if (distance3d < bestDistance) {
        bestDistance = distance3d
        bestCamera = cameraIndex
      }
    }

    if (bestCamera !== null) {
      console.log(`   🏆 [FALLBACK SELECTION] Best camera: ${bestCamera} (3D distance: ${bestDistance.toFixed(2)}m)`)
    }
  }

  return bestCamera
}

